import React, { useRef, ReactElement, useEffect } from 'react';
import { Box } from '@wds/box';
import { Typography } from '@wds/typography';
import { Link } from '@wds/link';
import { IconArrowBox } from '@wds/icons';

import { useHeaderTheme } from '../../theme';
import SolutionsIcon from './assets/Solutions';
import AudienceIcon from './assets/Audience';
import useStyles from './useStyles';

type PanelProps = {
  show: boolean;
  onToggle: () => void;
};
const ExplorePanel = ({ show, onToggle }: PanelProps): ReactElement | null => {
  const ref = useRef<HTMLDivElement>(null);
  const theme = useHeaderTheme();
  const { panel, title, leftBox, rightBox } = useStyles({ theme });

  useEffect(() => {
    if (show) {
      ref.current?.focus();
    }
  }, [show]);

  if (!show) {
    return null;
  }

  return (
    <div
      role="menu"
      tabIndex={-1}
      ref={ref}
      onBlur={e => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          onToggle();
        }
      }}
    >
      <Box display="flex" justifyContent="space-between" className={panel}>
        <Box pt={6} pr={2} pb={4} pl={6} className={leftBox}>
          <Box display="flex" mb={4} alignItems="center" className={title}>
            <Box display="inline" mr={2}>
              <AudienceIcon />
            </Box>
            <Typography weight="medium" variant="body">
              Audience
            </Typography>
          </Box>
          <Typography variant="bodySm">
            Survey people who live, work, and do what matters to you. Reach
            audiences in 130+ countries and get responses quickly.
          </Typography>
          <Box mt={3}>
            <Typography variant="bodySm" color="link" weight="medium">
              <Link
                underline={false}
                href="https://www.surveymonkey.com/collect/audience/calculator/preview?ut_source=header&ut_source3=buy_targeted_responses"
                target="_blank"
              >
                Buy targeted responses
              </Link>
            </Typography>
          </Box>
        </Box>
        <Box flexItemGrow pt={6} pr={5} pb={4} pl={6} className={rightBox}>
          <Box className={title} display="flex" mb={4} alignItems="center">
            <Box mr={2}>
              <SolutionsIcon />
            </Box>
            <Typography weight="medium" variant="body">
              Brand and market research
            </Typography>
          </Box>
          <Box mb={4}>
            <Typography variant="bodySm">
              Discover expert-designed solutions to gather feedback on your
              ideas, products, and business. Get valuable insights delivered in
              days.
            </Typography>
          </Box>
          <Box mb={2}>
            <Typography variant="bodySm" color="link">
              <Link
                underline={false}
                href="https://www.surveymonkey.com/market-research/solutions/brand-tracker/?ut_source=header&ut_source3=trackers"
                target="_blank"
              >
                Brand tracking
              </Link>
            </Typography>
          </Box>
          <Box mb={2}>
            <Typography variant="bodySm" color="link">
              <Link
                underline={false}
                href="https://www.surveymonkey.com/market-research/solutions/ad-creative-analysis/?ut_source=header&ut_source3=ad_creative_analysis"
                target="_blank"
              >
                Ad testing
              </Link>
            </Typography>
          </Box>
          <Box mb={2}>
            <Typography variant="bodySm" color="link">
              <Link
                underline={false}
                href="https://www.surveymonkey.com/market-research/solutions/idea-screening/?ut_source=header&ut_source3=idea_screener"
                target="_blank"
              >
                Idea screening
              </Link>
            </Typography>
          </Box>
          <Box mb={3}>
            <Typography variant="bodySm" color="link">
              <Link
                underline={false}
                href="https://www.surveymonkey.com/market-research/solutions/price-optimization/?ut_source=header&ut_source3=van_west"
                target="_blank"
              >
                Price optimization
              </Link>
            </Typography>
          </Box>
          <Typography variant="bodySm" color="link" weight="medium">
            <Link
              underline={false}
              href="https://www.surveymonkey.com/market-research/solutions/get-started/?ut_source=header"
              target="_blank"
            >
              Explore more solutions
              <Box display="inline" ml={4}>
                <IconArrowBox title="link icon" />
              </Box>
            </Link>
          </Typography>
        </Box>
      </Box>
    </div>
  );
};

export default ExplorePanel;
